import React, { useEffect, useRef, useState } from "react";
import { useRoute } from "@react-navigation/native";
import { RootRouteProps } from "routers/types/stack";

import { TextInput, View } from "react-native";
import {
  Input,
  Error,
  Toast,
  Header,
  Loading,
  TextArea,
  CheckBox,
  InputLabel,
  TimePicker,
  DatePicker,
} from "components";
import DetailsTemplate from "screens/components/DetailsTemplate";

import { useForm, Controller } from "react-hook-form";

import { strings } from "assets/constants";
import { alertPermission, dateToString, isFuture, mq } from "utils";

import {
  useReminder,
  useAddReminder,
  useEditReminder,
  useDeleteReminder,
} from "clients/apis";
import { _goToBack, _pop } from "routers/helper";
import { useChmod } from "hooks";

interface FormDataT {
  title: string;
  date: string;
  time: string;
  descriptions: string;
}

export default function Reminder() {
  const { chmod } = useChmod();

  const {
    popCount,
    customer,
    data: routeParamData,
    seller,
  } = useRoute<RootRouteProps<"reminder">>().params;
  const initial = routeParamData as ReminderT;
  const headerTitle = initial
    ? strings?.edit_reminder +
      " " +
      (initial?.customer ? initial?.customer?.name : initial?.admin?.name)
    : strings.add_reminder_for + " " + (seller ? seller?.name : customer?.name);

  const { isFetching, data, error, isError, refetch, isRefetching } =
    useReminder({ id: initial?.id, type: initial?.admin ? "task" : undefined });

  const titleInput = useRef<TextInput>();
  const descriptionsInput = useRef<TextInput>();

  const [done, setDone] = useState<0 | 1>(0);
  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormDataT>({
    defaultValues: {
      title: data?.data?.title,
      descriptions: data?.data?.description,
      date: data?.data?.remind_date,
      time: data?.data?.remind_time,
    },
  });

  const _onDoneTitleInput = () => descriptionsInput.current?.focus();
  const _onDoneDescriptionsInput = () => {};

  useEffect(() => {
    if (data) {
      setValue("title", data.data?.title);
      setValue("descriptions", data.data?.description);
      setValue("date", data.data?.remind_date);
      setValue("time", data.data?.remind_time);
      setDone(data?.data?.status === "FOLLOW" ? 1 : 0);
    }
  }, [data, setValue]);

  const addReminderMut = useAddReminder();
  const editReminderMut = useEditReminder();
  const deleteReminderMut = useDeleteReminder();

  const _onSubmit = () => {
    if (
      !isFuture(getValues().time) &&
      dateToString(new Date()) === getValues().date
    ) {
      Toast.warning({ message: strings.invalid_time });
      return;
    }

    const postData: AddReminderReqT = {
      title: getValues().title,
      description: getValues().descriptions,
      remind_at: getValues().date + " " + getValues().time,
      ...(seller
        ? { type: "task", admin_id: seller.id }
        : { customer_id: customer?.id }),
    };

    addReminderMut.mutate(postData, {
      onSuccess: () => _pop(popCount ? popCount : 2),
    });
  };

  const _onEdit = () => {
    const { title, descriptions, date, time } = getValues();

    const {
      title: dTitle,
      description,
      remind_date,
      remind_time,
      status,
    } = data?.data;

    if (
      title === dTitle &&
      descriptions === description &&
      date === remind_date &&
      time === remind_time &&
      ((status === "FOLLOW" && done === 1) ||
        (status !== "FOLLOW" && done === 0))
    ) {
      _goToBack();
      return;
    }

    const postData: EditReminderReqT = {
      title: getValues().title,
      description: getValues().descriptions,
      remind_at: getValues().date + " " + getValues().time,
      status: done === 1 ? "FOLLOW" : "FOLLOWING",
      ...(initial?.admin ? { type: "task" } : {}),
    };
    editReminderMut.mutate(
      { id: data?.data?.id, data: postData },
      { onSuccess: _goToBack }
    );
  };

  const _onDeleteConfirm = (close: () => void) =>
    deleteReminderMut.mutate(data?.data.id, {
      onSuccess: () => {
        close();
        _goToBack();
      },
    });

  const _renderContent = () => {
    if (isFetching) {
      return <Loading style={{ marginTop: mq(20) }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    return (
      <DetailsTemplate
        onDelete={_onDeleteConfirm}
        deletable={data && chmod("reminder", "DELETE") ? true : false}
        deleteLoading={deleteReminderMut?.isLoading}
        submitLabel={!data ? strings.submit_info : strings.edit_info}
        submitLoading={addReminderMut?.isLoading || editReminderMut?.isLoading}
        onSubmit={
          data
            ? chmod("reminder", "PUT")
              ? handleSubmit(_onEdit)
              : alertPermission
            : chmod("reminder", "POST")
            ? handleSubmit(_onSubmit)
            : alertPermission
        }
      >
        <InputLabel label={strings.title} />
        <Controller
          control={control}
          name="title"
          rules={{
            required: {
              value: true,
              message: strings.reminder_title_is_required,
            },
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              inputRef={titleInput}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              placeholder={strings.insert_reminder_title}
              icon={{ name: "edit-3" }}
              onEndEditing={_onDoneTitleInput}
              error={errors.title?.message as string}
            />
          )}
        />

        <InputLabel label={strings.descriptions} />
        <Controller
          control={control}
          name="descriptions"
          render={({ field: { onChange, onBlur, value } }) => (
            <TextArea
              inputRef={descriptionsInput}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              placeholder={strings.insert_reminder_descriptions}
              icon={{ name: "align-right" }}
              onEndEditing={_onDoneDescriptionsInput}
            />
          )}
        />
        <InputLabel label={strings.date} />
        <Controller
          control={control}
          name="date"
          rules={{
            required: {
              value: true,
              message: strings.reminder_date_is_required,
            },
          }}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              onSelect={onChange}
              value={value}
              minimumDate={dateToString()}
              placeholder={strings.pick_reminder_date}
              error={errors.date?.message as string}
            />
          )}
        />

        <InputLabel label={strings.time} />
        <Controller
          control={control}
          name="time"
          rules={{
            required: {
              value: true,
              message: strings.reminder_time_is_required,
            },
          }}
          render={({ field: { onChange, value } }) => (
            <TimePicker
              value={value}
              onSelect={onChange}
              error={errors.time?.message as string}
            />
          )}
        />

        <View style={{ height: 40 }} />

        {data && (
          <CheckBox title={strings.done} value={done} onChange={setDone} />
        )}
      </DetailsTemplate>
    );
  };

  return (
    <>
      <Header title={headerTitle} />
      {_renderContent()}
    </>
  );
}
